import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Checkbox,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Box,
  Chip,
  Skeleton,
  TextField,
  InputAdornment,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  MoreVert,
  Search,
  FilterList,
  GetApp,
} from '@mui/icons-material';
import { brandColors, designTokens } from '~/presentation/themes/modernTheme';

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: designTokens.borderRadius.lg,
  border: `1px solid ${brandColors.neutral[200]}`,
  boxShadow: designTokens.shadows.sm,
  backgroundColor: '#ffffff',
  overflow: 'hidden',
}));

const StyledTable = styled(Table)(({ theme }) => ({
  '& .MuiTableCell-root': {
    fontFamily: designTokens.typography.fontFamily,
    borderBottom: `1px solid ${brandColors.neutral[100]}`,
  },
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  backgroundColor: brandColors.neutral[50],
  
  '& .MuiTableCell-head': {
    fontWeight: designTokens.typography.fontWeights.semibold,
    fontSize: '0.875rem',
    color: brandColors.neutral[700],
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    padding: `${designTokens.spacing.md}px ${designTokens.spacing.lg}px`,
    borderBottom: `2px solid ${brandColors.neutral[200]}`,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme, clickable }) => ({
  cursor: clickable ? 'pointer' : 'default',
  transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
  
  '&:hover': {
    backgroundColor: clickable ? brandColors.primary[25] : brandColors.neutral[25],
  },
  
  '&.Mui-selected': {
    backgroundColor: brandColors.primary[50],
    '&:hover': {
      backgroundColor: brandColors.primary[75],
    },
  },
  
  '& .MuiTableCell-body': {
    fontSize: '0.875rem',
    color: brandColors.neutral[700],
    padding: `${designTokens.spacing.md}px ${designTokens.spacing.lg}px`,
  },
}));

const StyledTableCell = styled(TableCell)(({ theme, align }) => ({
  textAlign: align || 'left',
}));

const StyledChip = styled(Chip)(({ theme, variant, color }) => {
  const getChipStyles = () => {
    switch (color) {
      case 'success':
        return {
          backgroundColor: brandColors.success[100],
          color: brandColors.success[700],
          border: `1px solid ${brandColors.success[200]}`,
        };
      case 'warning':
        return {
          backgroundColor: brandColors.warning[100],
          color: brandColors.warning[700],
          border: `1px solid ${brandColors.warning[200]}`,
        };
      case 'error':
        return {
          backgroundColor: brandColors.error[100],
          color: brandColors.error[700],
          border: `1px solid ${brandColors.error[200]}`,
        };
      case 'info':
        return {
          backgroundColor: brandColors.primary[100],
          color: brandColors.primary[700],
          border: `1px solid ${brandColors.primary[200]}`,
        };
      default:
        return {
          backgroundColor: brandColors.neutral[100],
          color: brandColors.neutral[700],
          border: `1px solid ${brandColors.neutral[200]}`,
        };
    }
  };
  
  return {
    height: 24,
    fontSize: '0.75rem',
    fontFamily: designTokens.typography.fontFamily,
    fontWeight: designTokens.typography.fontWeights.medium,
    borderRadius: designTokens.borderRadius.full,
    ...getChipStyles(),
  };
});

const TableToolbar = styled(Box)(({ theme }) => ({
  padding: designTokens.spacing.lg,
  borderBottom: `1px solid ${brandColors.neutral[200]}`,
  backgroundColor: '#ffffff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: designTokens.spacing.md,
  flexWrap: 'wrap',
}));

const ModernTable = ({
  columns = [],
  data = [],
  loading = false,
  selectable = false,
  sortable = true,
  searchable = true,
  filterable = false,
  exportable = false,
  pagination = true,
  rowsPerPageOptions = [5, 10, 25, 50],
  defaultRowsPerPage = 10,
  onRowClick,
  onRowSelect,
  onSort,
  onSearch,
  onFilter,
  onExport,
  actions = [],
  title,
  subtitle,
  emptyMessage = 'Nenhum dado encontrado',
  className,
  sx,
  ...props
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
  const [orderBy, setOrderBy] = useState('');
  const [order, setOrder] = useState('asc');
  const [selected, setSelected] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuRowId, setMenuRowId] = useState(null);
  
  // Filtrar dados baseado na busca
  const filteredData = useMemo(() => {
    if (!searchTerm) return data;
    
    return data.filter(row => {
      return columns.some(column => {
        const value = row[column.field];
        if (value === null || value === undefined) return false;
        return value.toString().toLowerCase().includes(searchTerm.toLowerCase());
      });
    });
  }, [data, searchTerm, columns]);
  
  // Ordenar dados
  const sortedData = useMemo(() => {
    if (!orderBy) return filteredData;
    
    return [...filteredData].sort((a, b) => {
      const aValue = a[orderBy];
      const bValue = b[orderBy];
      
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return order === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      if (aValue < bValue) return order === 'asc' ? -1 : 1;
      if (aValue > bValue) return order === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredData, orderBy, order]);
  
  // Paginar dados
  const paginatedData = useMemo(() => {
    if (!pagination) return sortedData;
    
    const startIndex = page * rowsPerPage;
    return sortedData.slice(startIndex, startIndex + rowsPerPage);
  }, [sortedData, page, rowsPerPage, pagination]);
  
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    setOrder(newOrder);
    setOrderBy(property);
    
    if (onSort) {
      onSort(property, newOrder);
    }
  };
  
  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = paginatedData.map(row => row.id);
      setSelected(newSelected);
      if (onRowSelect) {
        onRowSelect(newSelected);
      }
    } else {
      setSelected([]);
      if (onRowSelect) {
        onRowSelect([]);
      }
    }
  };
  
  const handleRowSelect = (id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];
    
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    
    setSelected(newSelected);
    if (onRowSelect) {
      onRowSelect(newSelected);
    }
  };
  
  const handleRowClick = (row) => {
    if (onRowClick) {
      onRowClick(row);
    }
  };
  
  const handleMenuOpen = (event, rowId) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setMenuRowId(rowId);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuRowId(null);
  };
  
  const handleActionClick = (action, row) => {
    handleMenuClose();
    if (action.onClick) {
      action.onClick(row);
    }
  };
  
  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    setPage(0); // Reset to first page when searching
    
    if (onSearch) {
      onSearch(value);
    }
  };
  
  const isSelected = (id) => selected.indexOf(id) !== -1;
  
  const renderCellContent = (row, column) => {
    const value = row[column.field];
    
    if (column.renderCell) {
      return column.renderCell({ row, value });
    }
    
    if (column.type === 'chip') {
      const chipProps = column.getChipProps ? column.getChipProps(value, row) : {};
      return (
        <StyledChip
          label={value}
          size="small"
          {...chipProps}
        />
      );
    }
    
    if (column.type === 'currency') {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(value || 0);
    }
    
    if (column.type === 'date') {
      return value ? new Date(value).toLocaleDateString('pt-BR') : '-';
    }
    
    if (column.type === 'boolean') {
      return (
        <StyledChip
          label={value ? 'Sim' : 'Não'}
          color={value ? 'success' : 'default'}
          size="small"
        />
      );
    }
    
    return value || '-';
  };
  
  const renderSkeletonRows = () => {
    return Array.from({ length: rowsPerPage }).map((_, index) => (
      <StyledTableRow key={`skeleton-${index}`}>
        {selectable && (
          <StyledTableCell padding="checkbox">
            <Skeleton variant="rectangular" width={18} height={18} />
          </StyledTableCell>
        )}
        {columns.map((column) => (
          <StyledTableCell key={column.field} align={column.align}>
            <Skeleton variant="text" width="80%" />
          </StyledTableCell>
        ))}
        {actions.length > 0 && (
          <StyledTableCell align="right">
            <Skeleton variant="circular" width={24} height={24} />
          </StyledTableCell>
        )}
      </StyledTableRow>
    ));
  };
  
  return (
    <Box className={`modern-table ${className || ''}`} sx={sx}>
      {/* Toolbar */}
      {(title || searchable || filterable || exportable) && (
        <TableToolbar>
          <Box>
            {title && (
              <Typography
                variant="h6"
                sx={{
                  fontFamily: designTokens.typography.fontFamily,
                  fontWeight: designTokens.typography.fontWeights.semibold,
                  color: brandColors.neutral[800],
                  mb: subtitle ? 0.5 : 0,
                }}
              >
                {title}
              </Typography>
            )}
            {subtitle && (
              <Typography
                variant="body2"
                sx={{
                  fontFamily: designTokens.typography.fontFamily,
                  color: brandColors.neutral[600],
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {searchable && (
              <TextField
                size="small"
                placeholder="Buscar..."
                value={searchTerm}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: brandColors.neutral[400] }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  minWidth: 200,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: designTokens.borderRadius.md,
                  },
                }}
              />
            )}
            
            {filterable && (
              <IconButton
                onClick={onFilter}
                sx={{
                  color: brandColors.neutral[600],
                  '&:hover': {
                    backgroundColor: brandColors.neutral[100],
                  },
                }}
              >
                <FilterList />
              </IconButton>
            )}
            
            {exportable && (
              <IconButton
                onClick={onExport}
                sx={{
                  color: brandColors.neutral[600],
                  '&:hover': {
                    backgroundColor: brandColors.neutral[100],
                  },
                }}
              >
                <GetApp />
              </IconButton>
            )}
          </Box>
        </TableToolbar>
      )}
      
      {/* Table */}
      <StyledTableContainer>
        <StyledTable {...props}>
          <StyledTableHead>
            <TableRow>
              {selectable && (
                <StyledTableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selected.length > 0 && selected.length < paginatedData.length}
                    checked={paginatedData.length > 0 && selected.length === paginatedData.length}
                    onChange={handleSelectAllClick}
                    sx={{
                      color: brandColors.neutral[400],
                      '&.Mui-checked': {
                        color: brandColors.primary[500],
                      },
                    }}
                  />
                </StyledTableCell>
              )}
              
              {columns.map((column) => (
                <StyledTableCell
                  key={column.field}
                  align={column.align}
                  sortDirection={orderBy === column.field ? order : false}
                >
                  {sortable && column.sortable !== false ? (
                    <TableSortLabel
                      active={orderBy === column.field}
                      direction={orderBy === column.field ? order : 'asc'}
                      onClick={() => handleRequestSort(column.field)}
                      sx={{
                        '& .MuiTableSortLabel-icon': {
                          color: `${brandColors.primary[500]} !important`,
                        },
                      }}
                    >
                      {column.headerName}
                    </TableSortLabel>
                  ) : (
                    column.headerName
                  )}
                </StyledTableCell>
              ))}
              
              {actions.length > 0 && (
                <StyledTableCell align="right">
                  Ações
                </StyledTableCell>
              )}
            </TableRow>
          </StyledTableHead>
          
          <TableBody>
            {loading ? (
              renderSkeletonRows()
            ) : paginatedData.length === 0 ? (
              <StyledTableRow>
                <StyledTableCell
                  colSpan={columns.length + (selectable ? 1 : 0) + (actions.length > 0 ? 1 : 0)}
                  align="center"
                  sx={{ py: 4 }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: brandColors.neutral[500],
                      fontFamily: designTokens.typography.fontFamily,
                    }}
                  >
                    {emptyMessage}
                  </Typography>
                </StyledTableCell>
              </StyledTableRow>
            ) : (
              paginatedData.map((row) => {
                const isItemSelected = isSelected(row.id);
                
                return (
                  <StyledTableRow
                    key={row.id}
                    hover
                    clickable={!!onRowClick}
                    onClick={() => handleRowClick(row)}
                    selected={isItemSelected}
                  >
                    {selectable && (
                      <StyledTableCell padding="checkbox">
                        <Checkbox
                          checked={isItemSelected}
                          onChange={() => handleRowSelect(row.id)}
                          onClick={(e) => e.stopPropagation()}
                          sx={{
                            color: brandColors.neutral[400],
                            '&.Mui-checked': {
                              color: brandColors.primary[500],
                            },
                          }}
                        />
                      </StyledTableCell>
                    )}
                    
                    {columns.map((column) => (
                      <StyledTableCell key={column.field} align={column.align}>
                        {renderCellContent(row, column)}
                      </StyledTableCell>
                    ))}
                    
                    {actions.length > 0 && (
                      <StyledTableCell align="right">
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, row.id)}
                          sx={{
                            color: brandColors.neutral[400],
                            '&:hover': {
                              color: brandColors.neutral[600],
                              backgroundColor: brandColors.neutral[100],
                            },
                          }}
                        >
                          <MoreVert />
                        </IconButton>
                      </StyledTableCell>
                    )}
                  </StyledTableRow>
                );
              })
            )}
          </TableBody>
        </StyledTable>
      </StyledTableContainer>
      
      {/* Pagination */}
      {pagination && !loading && (
        <TablePagination
          component="div"
          count={sortedData.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={rowsPerPageOptions}
          labelRowsPerPage="Linhas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          sx={{
            borderTop: `1px solid ${brandColors.neutral[200]}`,
            '& .MuiTablePagination-toolbar': {
              fontFamily: designTokens.typography.fontFamily,
            },
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              fontSize: '0.875rem',
              color: brandColors.neutral[600],
            },
          }}
        />
      )}
      
      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            borderRadius: designTokens.borderRadius.md,
            boxShadow: designTokens.shadows.lg,
            border: `1px solid ${brandColors.neutral[200]}`,
            minWidth: 150,
          },
        }}
      >
        {actions.map((action, index) => {
          const currentRow = paginatedData.find(row => row.id === menuRowId);
          
          return (
            <MenuItem
              key={index}
              onClick={() => handleActionClick(action, currentRow)}
              disabled={action.disabled && action.disabled(currentRow)}
              sx={{
                fontSize: '0.875rem',
                fontFamily: designTokens.typography.fontFamily,
                color: action.color === 'error' ? brandColors.error[600] : brandColors.neutral[700],
                '&:hover': {
                  backgroundColor: action.color === 'error' ? brandColors.error[50] : brandColors.neutral[50],
                },
              }}
            >
              {action.icon && (
                <Box sx={{ mr: 1, display: 'flex' }}>
                  {action.icon}
                </Box>
              )}
              {action.label}
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );
};

ModernTable.displayName = 'ModernTable';

export default ModernTable;